import { nullish } from '../../shared/types/nullish.ts';
import { Tool } from '../components/operation-dialog/operationDraft.ts';


// const NBSP_D = '\u00A0';//non-breaking space
const NBSP_N = '\u202F';//Narrow non-breaking space


export function fieldIsLoadingText(): string {
    return '...';
}

export function formatProduct(product: string | undefined): string {
    if (!product) return '';
    return product;
}

export function formatBatchStart(batchStart: Date | undefined): string {
    if (!batchStart) return '';
    return formatDatetime(batchStart);
}

export function formatBatchEnd(batchEnd: Date): string {
    return formatDatetime(batchEnd);
}

export function formatSetupTime(setupTime: number | null | undefined): string {
    if (!setupTime) return '';
    return formatDuration(setupTime, true, false);
}

export function formatLatestData(latestData: Date): string {
    return formatTime(latestData);
}

export function formatKpiAllAndRecent(overall: string | nullish, recent: string | nullish): string {
    if (!overall) return '';
    return overall + (recent ? ` (${recent})` : '');
}

export function formatCycleTime(cycleTime: number | null | undefined): string {
    if (!cycleTime) return '';
    return formatDuration(cycleTime, false, true);
}

export function formatPartsPerHour(partsPerHour: number | null | undefined): string {
    if (partsPerHour == null) return '';
    const partsPerHourRounded = Math.round(partsPerHour * 10) / 10.0;
    return `${partsPerHourRounded}`;
}

export function formatPartsPerDay(partsPerHour: number | null | undefined): string {
    if (partsPerHour == null) return '';
    const partsPerDay = partsPerHour * 24;
    const partsPerDayRounded = Math.round(partsPerDay);
    return `${partsPerDayRounded}`;
}

export function formatQuality(quality: number | null | undefined): string {
    if (quality == null) return '';
    return `${(quality * 100).toFixed(0)}%`;
}

export function formatPartsProduced(partsProduced: number | null | undefined): string {
    if (partsProduced == null) return '';

    const partsProducedRounded = Math.round(partsProduced);
    return `${partsProducedRounded}`;
}

export function formatEfficiency(efficiency: number | null | undefined): string {
    if (efficiency == null) return '';
    return `${Math.round(efficiency * 100)}%`;
}

export function formatPricePerPart(pricePerPart: number | null | undefined): string {
    if (!pricePerPart) return '';
    return pricePerPart + '';
}

export function formatEurosPerHour(eurosPerHour: number | null | undefined): string {
    if (!eurosPerHour) return '';
    return eurosPerHour.toFixed(2);
}

export function formatOperationDetails(
    operationDetails: string[] | null,
    toolChangeAlarm: 'active' | 'fixed' | undefined,
    collateralDamage: Tool[] | undefined
): string {
    if (operationDetails == null) return '';

    const toolChangeIcon = toolChangeAlarm === 'active' ? ' &#128308;'
        : toolChangeAlarm === 'fixed' ? ' &#x26AA;'
            : '';
    const mainText = operationDetails.join(' > ') + toolChangeIcon;

    const collateralList = collateralDamage?.map((it) => it.toolVariant + ' ' + it.toolNumber);
    const collateralText = collateralList && collateralList?.length > 0
        ? `(Collateral: ${collateralList.join('; ')})`
        : null;

    return [mainText, collateralText].filter(it => it).join('<br>');
}

export function formatBrokenToolFrequency(frequency: number | null): string {
    if (!frequency) return '';

    return frequency <= 1.0 ? frequency.toFixed(1) : frequency.toFixed(0);
}

export function formatDatetime(
    datetime: Date | null | undefined,
    options?: { nonBreakingSpace: boolean }
): string {
    if (!datetime) return '';
    let formatted = datetime.toLocaleTimeString('et', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
    if (options && options.nonBreakingSpace) {
        formatted = formatted.replace(/\s/g, NBSP_N);
    }
    return formatted;
}

export function formatTime(datetime: Date | undefined): string {
    if (!datetime) return '';
    return datetime.toLocaleTimeString('et', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export function formatDate(date: Date | undefined): string {
    if (!date) return '';
    return date.toLocaleDateString('et', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}

export function formatBatchTimeRemaining(
    optimisticTimeRemaining: number | null | undefined,
    realisticTimeRemaining: number | null | undefined,
): string {
    const optimisticFormatted = formatBatchTimeRemainingSingle(optimisticTimeRemaining);
    const realisticFormatted = formatBatchTimeRemainingSingle(realisticTimeRemaining);
    if (realisticFormatted) {
        return `${optimisticFormatted}\u00A0\u00A0(${realisticFormatted})`;
    } else {
        return optimisticFormatted;
    }
}

export function formatBatchTimeRemainingSingle(millisRemaining: number | null | undefined): string {
    if (millisRemaining == null) return '';

    const hoursRemaining = millisRemaining / 3600_000;
    const hoursRounded = hoursRemaining >= 0 ? Math.floor(hoursRemaining) : Math.ceil(hoursRemaining);
    if (hoursRounded !== 0) return `${hoursRounded}h`;

    const minutesRemaining = millisRemaining / 60_000;
    const minutesRounded = minutesRemaining >= 0 ? Math.floor(minutesRemaining) : Math.ceil(minutesRemaining);
    return `${minutesRounded}m`;
}

export function formatDuration(timeMillis: number | null, alwaysShowHours: boolean, showSeconds: boolean): string {
    if (timeMillis == null) return '';

    const totalSeconds = Math.round(Math.abs(timeMillis / 1000));

    const hours = Math.floor(totalSeconds / 3600);
    const hoursText = (hours || alwaysShowHours) ? `${hours.toString()}h` : '';

    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const minutesPadding = (hoursText && minutes < 10 ? '0' : '');
    const minutesText = minutesPadding + minutes.toString() + 'm';

    const seconds = totalSeconds % 60;
    let secondsText;
    if (showSeconds) {
        const secondsPadding = (seconds < 10 ? '0' : '');
        secondsText = secondsPadding + seconds.toString() + 's';
    } else {
        secondsText = '';
    }

    const minus = timeMillis < 0 ? '-' : '';
    const out = minus + [hoursText, minutesText, secondsText].filter((it) => it).join(NBSP_N);
    return out;
}
